<template>
  <header
    ref="header"
    class="header"
    :class="{
      'header-white-variant': headerScrollVariant,
      'header-scroll': scrollVariant && !grayVariant,
      'header-white-variant gray-scroll-variant': grayScrollVariant,
      'header-white-variant gray-variant': grayVariant,
    }"
  >
    <div class="container">
      <nuxt-link
        to="/"
        aria-label="Proxify homepage"
        @click="closeMenuOnNavigatingSameScreen('index')"
      >
        <LogoDarkIcon
          v-if="darkIcon"
          class="logo"
        />
        <LogoIcon
          v-else
          class="logo logo-white"
        />
      </nuxt-link>
      <div class="mobile-menu">
        <ApplyButton
          cta-section="top-nav"
          :page-name="pageName"
          class="btn btn-apply"
        />
        <CrossIcon
          v-if="mobileMenuOpened"
          class="cross-icon"
          @click="toggleMenu"
        />
        <MenuIcon
          v-else
          class="menu-icon"
          @click="toggleMenu"
        />
      </div>
      <nav :class="{ 'nav-opened': mobileMenuOpened }">
        <ul>
          <li class="menu-title">Menu</li>
          <li>
            <nuxt-link
              aria-label="How it works"
              to="/how-it-works"
              class="link page-link"
              @click="closeMenuOnNavigatingSameScreen('how-it-works')"
            >
              How it works
            </nuxt-link>
          </li>
          <li class="journal-page-link">
            <nuxt-link
              aria-label="The journal"
              to="/journal"
              class="link page-link"
              @click="closeMenuOnNavigatingSameScreen('journal')"
            >
              The journal
            </nuxt-link>
            <div v-if="showJournalCategories">
              <nuxt-link to="/journal">All</nuxt-link>
              <nuxt-link
                :to="`/journal?category=${successCategory}`"
                @click.prevent="setCategory(successCategory)"
              >
                Developer success
              </nuxt-link>
              <nuxt-link
                v-for="(category, index) in journalCategories"
                :key="index"
                :to="`/journal?category=${category.slug}`"
                @click.prevent="setCategory(category.slug)"
              >
                {{ category.title }}
              </nuxt-link>
              <nuxt-link
                :to="`/journal?category=${downloadsCategory}`"
                @click.prevent="setCategory(downloadsCategory)"
              >
                Dev Downloads
              </nuxt-link>
            </div>
          </li>
          <li>
            <nuxt-link
              aria-label="Events"
              to="/events"
              class="link page-link events"
              @click="closeMenuOnNavigatingSameScreen('events')"
            >
              Events
            </nuxt-link>
          </li>
          <li>
            <nuxt-link
              aria-label="Jobs"
              to="/jobs"
              class="link page-link jobs"
              @click="closeMenuOnNavigatingSameScreen('jobs')"
            >
              Jobs
            </nuxt-link>
          </li>
          <li>
            <a
              aria-label="Help center"
              class="link page-link"
              target="_blank"
              :href="helpCenterUrl"
            >
              Help center
            </a>
          </li>
          <li>
            <ApplyButton
              cta-section="top-nav"
              :page-name="pageName"
              class="proxify-btn proxify-btn-white proxify-btn-no-icon"
            />
          </li>
          <li>
            <a
              aria-label="Login"
              href="https://app.proxify.io/login"
              class="link login-link"
              @click="onLoginClick"
            >
              LOG IN
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </header>
</template>

<script>
import ApplyButton from '~/components/Common/ApplyButton/ApplyButton.vue';
import LogoIcon from '~/components/Common/Svg/LogoIcon.vue';
import MenuIcon from '~/components/Common/Svg/MenuIcon.vue';
import CrossIcon from '~/components/Common/Svg/CrossIcon.vue';
import LogoDarkIcon from '~/components/Common/Svg/LogoDarkIcon.vue';
import slugRouteName from '~/mixins/slugRouteName';
import { useJournalStore, DEFAULT_CATEGORIES } from '~/stores/journal';

const { DEV_SUCCESS_CATEGORY, DEV_DOWNLOADS_CATEGORY } = DEFAULT_CATEGORIES;

export default {
  name: 'AppHeader',
  components: { ApplyButton, LogoIcon, MenuIcon, CrossIcon, LogoDarkIcon },
  mixins: [slugRouteName],
  props: {
    headerScrollVariant: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mobileMenuOpened: false,
      scrollTop: 0,
      scrollBottom: 0,
      successCategory: DEV_SUCCESS_CATEGORY,
      downloadsCategory: DEV_DOWNLOADS_CATEGORY,
      journalStore: useJournalStore(),
    };
  },
  computed: {
    scrollVariant() {
      if (['privacy', 'cookie-policy'].includes(this.$route.name)) {
        return true;
      }
      const scrollPercent = (this.scrollTop / this.scrollBottom) * 100;

      return scrollPercent > 0;
    },
    grayScrollVariant() {
      return ['journal'].includes(this.$route.name);
    },
    grayVariant() {
      return ['article-slug'].includes(this.$route.name);
    },
    showJournalCategories() {
      return ['article-slug'].includes(this.$route.name);
    },
    journalCategories() {
      return this.journalStore.categories;
    },
    darkIcon() {
      return (
        (this.scrollVariant ||
          this.headerScrollVariant ||
          this.grayVariant ||
          this.grayScrollVariant) &&
        !this.mobileMenuOpened
      );
    },
    helpCenterUrl() {
      const { helpCenterUrl } = useRuntimeConfig().public;

      return helpCenterUrl;
    },
  },
  watch: {
    $route() {
      this.mobileMenuOpened = false;
    },
    mobileMenuOpened(mobileMenuOpened) {
      if (mobileMenuOpened) {
        document.querySelector('body').classList.add('body-noscroll');
      } else {
        document.querySelector('body').classList.remove('body-noscroll');
      }
    },
  },
  mounted() {
    this.onScroll();
    document.querySelector('body').classList.remove('body-noscroll');
    document.addEventListener('scroll', this.onScroll);
  },
  beforeUnmount() {
    document.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;

      this.scrollBottom =
        (document.documentElement.scrollHeight || document.body.scrollHeight) -
        document.documentElement.clientHeight;
    },
    toggleMenu() {
      this.mobileMenuOpened = !this.mobileMenuOpened;
    },
    closeMenuOnNavigatingSameScreen(navigateTo) {
      if (this.mobileMenuOpened && navigateTo === this.$route.name) {
        this.mobileMenuOpened = false;
      }
    },
    setCategory(category = null) {
      this.journalStore.setCategory(category);
    },
    onLoginClick() {
      this.$analytics.track('CTA Clicked', {
        cta_name: 'Log In',
        page_name: this.pageName,
        section: 'top-nav',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AppHeader.scss';
</style>
